/**
 * This Panel shows when a User has done nothing related to Advisory
 *
 */

import {
  Separator,
  Badge,
  Image,
  Button,
  Text,
  Link,
  Icon,
  Card,
  CardColors,
  Alert,
  Dialog,
} from '@/ui'
import CalendlyPopupModal from '@/components/CalendlyPopupModal'
import { useEffect, useState } from 'react'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { createIntroCallScheduledAfterEffects } from '../service'
import { useReselector } from '@/utils/sharedHooks'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { AdvisorHelpSummaryCard } from './PreIntroCallPanel'
import { selectFinancialAdvisoryProfile } from '../financialAdvisory.selectors'
import { FinancialAdvisoryProfilesStatus } from '../financialAdvisory.slice'
import { TextColors, TypographyStyles } from '@/styles'
import { faCheck } from '@fortawesome/pro-regular-svg-icons'
const INTRO_CALL_CALENDLY_URL =
  'https://calendly.com/d/cmjd-h62-zbx/heard-intro-advisory-call'

const ScheduleCallCard = ({
  calendlyOpen,
  track,
  setCalendlyOpen,
  introCallScheduledAfterEffects,
}: {
  calendlyOpen: boolean
  track: (arg0: string, arg1: { financial_advisory_step: string }) => void
  setCalendlyOpen: (arg0: boolean) => void
  introCallScheduledAfterEffects: (arg0: string | undefined) => void
}) => {
  return (
    <Card color={CardColors.EMPHASIS}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Text className="w-full mb-8" variant={TypographyStyles.H1}>
          Get Affordable, Easy-to-implement Financial Advisory with Heard
        </Text>
        <div className="flex flex-col lg:flex-row justify-center gap-12">
          <div className="bg-blush p-12 rounded-lg w-full lg:w-1/2 lg:ml-6">
            {[
              'Dedicated Uprise CFP® Advisor',
              'Personalized Financial Plan with unlimited revisions',
              'Goal progress tracking',
              'Unlimited Expert Support',
              '1:1 video call (add on, $150 per 45 minute call)',
            ].map((text, index) => (
              <Text
                key={index}
                variant={TypographyStyles.H3}
                className="flex items-center mb-4"
              >
                <Icon icon={faCheck} size="1x" className="text-forest mr-2" />
                {text}
              </Text>
            ))}
            <Separator className="my-6" />
            <div className="text-right">
              <Text variant={TypographyStyles.H1} className="text-brand">
                $29/month
              </Text>
              <Text variant={TypographyStyles.H3} className="text-brand">
                Or $300 billed annually
              </Text>
            </div>
          </div>
          <div className="w-full lg:w-1/2 lg:pl-12 flex flex-col justify-center">
            <Text variant={TypographyStyles.BODY_MD} className="mb-6">
              Other financial advisory services can cost $1,000-$6,000+ a year,
              and require you to send your business financial data. With Heard,
              you can easily add financial advisory onto your monthly or annual
              subscription, and cancel at anytime.
            </Text>
            {[
              'Free introductory consult',
              'Affordable cost',
              'Unlimited, expert support',
            ].map((text, index) => (
              <Text
                key={index}
                variant={TypographyStyles.H3}
                className="flex items-center mb-4"
              >
                <Icon icon={faCheck} size="1x" className="text-forest mr-2" />
                {text}
              </Text>
            ))}
            <Button
              className="w-full mt-6"
              onClick={() => {
                setCalendlyOpen(true)
                track('clicked schedule intro call button', {
                  financial_advisory_step: 'start schedule introductary call',
                })
              }}
            >
              Schedule your 20 Minute Free Intro Call
            </Button>
          </div>
        </div>
        {calendlyOpen && (
          <CalendlyPopupModal
            url={INTRO_CALL_CALENDLY_URL}
            open={calendlyOpen}
            onClose={() => setCalendlyOpen(false)}
            onScheduled={(eventUri: string | undefined) => {
              introCallScheduledAfterEffects(eventUri)
              track('scheduled introductory financial advisory', {
                financial_advisory_step: 'completed schedule introductory call',
              })
            }}
          />
        )}
      </div>
    </Card>
  )
}
const HowItWorksCard = () => {
  return (
    <Card color={CardColors.LEVEL_0}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Text className="mb-12" variant={TypographyStyles.H1}>
          Here&#39;s how it works:
        </Text>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            {
              title: "First Let's Chat",
              img: 'https://heard-images.s3.amazonaws.com/assets/advisor_piggy_bank.svg',
              desc: "Schedule a brief call to discuss your goals and see if we're a good fit.",
            },
            {
              title: 'Build Your Plan',
              img: 'https://heard-images.s3.amazonaws.com/assets/advisor_financials.svg',
              desc: 'Your Uprise advisor creates your personalized financial roadmap',
            },
            {
              title: 'Get Anytime Support',
              img: 'https://heard-images.s3.amazonaws.com/assets/anytime_support.svg',
              desc: 'Message anytime with your advisor to stay on track',
            },
            {
              title: 'Watch It Work',
              img: 'https://heard-images.s3.amazonaws.com/assets/metrics.svg',
              desc: 'See your financial goals become reality, step by step',
            },
          ].map(({ title, img, desc }, index) => (
            <div key={index} className="text-center">
              <Image
                className="flex justify-center mb-5 w-[100px]"
                src={img}
                alt="heard financial advisory"
              />
              <Text variant={TypographyStyles.H3} className="mb-2">
                {title}
              </Text>
              <Text variant={TypographyStyles.BODY_SM}>{desc}</Text>
            </div>
          ))}
        </div>
      </div>
    </Card>
  )
}

export const UpriseIntroCard = () => {
  return (
    <Card color={CardColors.LEVEL_1}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Text className="mb-6" variant={TypographyStyles.H2}>
          Who will I be working with?
        </Text>
        <Text className="mb-6" variant={TypographyStyles.BODY_MD}>
          Heard offers financial advisory in partnership with Uprise, a team of
          Certified Financial Planners that build and create customized
          financial plans based on your goals.
        </Text>
        <Image
          className="flex justify-center mb-4 w-[75px]"
          src="https://heard-images.s3.amazonaws.com/assets/uprise_team.svg"
          alt="uprise team"
        />
        <Image
          className="flex justify-center mb-4 w-[150px]"
          src="https://heard-images.s3.amazonaws.com/assets/uprise.svg"
          alt="uprise"
        />
      </div>
    </Card>
  )
}

const JoinedWaitlistModal = ({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <Dialog.Content className="max-w-md text-center">
        <Dialog.Title>Got it, you’ve been added to the waitlist!</Dialog.Title>
        <Image
          className="flex justify-center my-5"
          src="https://heard-images.s3.amazonaws.com/assets/check.svg"
          alt="check"
        />
        <Text variant={TypographyStyles.BODY_MD}>
          We are so glad you&#39;re interested in Financial Advisory. We have
          added your name to our waitlist, and we will notify you when spaces
          open up. Thank you for your understanding!
        </Text>
        <Button className="mt-6" variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Dialog.Content>
    </Dialog>
  )
}

export const WaitlistCard = ({
  waitlistJoined,
}: {
  waitlistJoined: boolean
}) => {
  return (
    <Card color={CardColors.LEVEL_1}>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Badge color={waitlistJoined ? 'green' : 'blue'}>
            {waitlistJoined ? 'Currently Waitlisted' : 'Now Waitlist Only'}
          </Badge>
          <Text variant={TypographyStyles.H2}>
            {waitlistJoined
              ? 'You’re on our waitlist!'
              : 'Our Introductory Calls Are Fully Booked Right Now'}
          </Text>
        </div>
        <Text variant={TypographyStyles.BODY_MD}>
          {waitlistJoined
            ? 'Thank you for letting us know of your interest. You are now on the waitlist, and we will let you know when spaces open up.'
            : 'Due to high demand, all available slots for our free 20-minute introductory calls have been booked.'}
        </Text>
      </div>
    </Card>
  )
}

export const IntroCallCTAPanel = () => {
  const track = useAnalyticsTrack()
  const pageView = useAnalyticsView()

  /** Key Variables */
  const currentUser = useReselector(getCurrentUser)
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const faProfile = useReselector(selectFinancialAdvisoryProfile)

  /** State Vars */
  const [calendlyOpen, setCalendlyOpen] = useState(false)

  /** Waitlist Vars */
  const isWaitlisted =
    faProfile?.status === FinancialAdvisoryProfilesStatus.waitlisted
  const [error] = useState('')
  const [waitlistJoined] = useState(Boolean(faProfile?.waitlistedAt))
  const [waitlistJoinedModalOpen, setWaitlistJoinedModalOpen] = useState(false)

  // Used to manage after effects of scheduling the intro call
  const introCallScheduledAfterEffects = async (
    eventUri: string | undefined
  ) => {
    track('scheduled intro advisory call', {
      user_id: currentUser?.id || null,
      event_uri: eventUri || null,
    })
    await createIntroCallScheduledAfterEffects({ currentUser })
    // Reload to properly view changes
    location.reload()
  }

  useEffect(() => {
    pageView('advisory: view intro call CTA page')
  }, [pageView])

  return (
    <>
      <div className="px-2">
        {isWaitlisted && (
          <div className="mb-4">
            <WaitlistCard waitlistJoined={waitlistJoined} />
          </div>
        )}
        {error && (
          <Alert
            variant="error"
            description="It looks like we encountered an error! Please refresh the page and try again."
          />
        )}

        <div className="flex flex-col lg:flex-row items-center lg:items-start gap-6">
          <div className="w-full lg:w-1/2 space-y-4">
            <Badge color="green">★ New Add On</Badge>
            <Text variant={TypographyStyles.H1}>
              Use Your Practice Revenue to Drive Your Financial Goals
            </Text>
            <Text variant={TypographyStyles.BODY_MD}>
              Whether you&#39;re just starting out, paying off debt, or wanting
              to build long-term wealth, we&#39;ve partnered with{' '}
              <Link newPage href="https://uprise.us/" variant="secondary">
                Uprise
              </Link>{' '}
              to help you get there. Your Uprise advisor uses your real practice
              data to create a financial plan that works for you.
            </Text>
            <Button
              className={isMobile ? 'w-full' : ''}
              onClick={() => {
                setCalendlyOpen(true)
                track('clicked schedule intro call button', {
                  financial_advisory_step: 'start schedule introductary call',
                })
              }}
            >
              Schedule your free introductory call
            </Button>
            <Text variant={TypographyStyles.H3} color={TextColors.MUTED}>
              No commitment - 100% free
            </Text>
          </div>

          <Image
            className="w-full lg:w-1/2"
            src="https://heard-images.s3.amazonaws.com/assets/advisory_screen.svg"
            alt="heard financial advisory"
          />
        </div>
      </div>

      <div className="my-6">
        <AdvisorHelpSummaryCard />
      </div>

      <div className="my-6">
        <ScheduleCallCard
          calendlyOpen={calendlyOpen}
          track={track}
          setCalendlyOpen={setCalendlyOpen}
          introCallScheduledAfterEffects={introCallScheduledAfterEffects}
        />
      </div>

      <div className="my-6">
        <HowItWorksCard />
      </div>

      {calendlyOpen && (
        <CalendlyPopupModal
          url={INTRO_CALL_CALENDLY_URL}
          open={calendlyOpen}
          onClose={() => setCalendlyOpen(false)}
          onScheduled={(eventUri: string | undefined) => {
            introCallScheduledAfterEffects(eventUri)
            track('scheduled introductory financial advisory', {
              financial_advisory_step: 'completed schedule introductory call',
            })
          }}
        />
      )}

      <div className="my-6">
        <UpriseIntroCard />
      </div>

      <JoinedWaitlistModal
        open={waitlistJoinedModalOpen}
        onClose={() => setWaitlistJoinedModalOpen(false)}
      />
    </>
  )
}
